'use client';

import { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import PropTypes from 'prop-types';

// Mock data for frontend testing
const MOCK_BOOKS = [
  {
    id: '1',
    slug: 'chronicles-of-faith',
    title: 'Chronicles of Faith',
    author: { name: 'Dr. Sarah Mitchell' },
    description: 'A comprehensive exploration of biblical faith through historical and contemporary perspectives.',
    coverImageUrl: 'https://picsum.photos/300/400?random=1',
    tags: ['Theology', 'Faith', 'History'],
    category: { name: 'Biblical Studies' },
    accessType: 'free',
    chapterCount: 12,
    createdAt: '2025-11-15T10:00:00Z',
  },
  {
    id: '2',
    slug: 'prayers-wisdom',
    title: 'Prayers of Wisdom',
    author: { name: 'Rev. John Thompson' },
    description: 'Daily prayers and devotional insights for spiritual growth and understanding.',
    coverImageUrl: 'https://picsum.photos/300/400?random=2',
    tags: ['Prayer', 'Devotional', 'Wisdom'],
    category: { name: 'Devotional' },
    accessType: 'premium',
    chapterCount: 30,
    createdAt: '2025-10-20T08:30:00Z',
  },
  {
    id: '3',
    slug: 'understanding-grace',
    title: 'Understanding Grace',
    author: { name: 'Pastor Michael Anderson' },
    description: 'An in-depth study of grace in Christian theology and practical application.',
    coverImageUrl: 'https://picsum.photos/300/400?random=3',
    tags: ['Grace', 'Theology', 'Christian Living'],
    category: { name: 'Theology' },
    accessType: 'free',
    chapterCount: 15,
    createdAt: '2025-09-10T14:20:00Z',
  },
  {
    id: '4',
    slug: 'biblical-leadership',
    title: 'Biblical Leadership Principles',
    author: { name: 'Dr. Elizabeth Carter' },
    description: 'Leadership lessons drawn from biblical characters and modern applications.',
    coverImageUrl: 'https://picsum.photos/300/400?random=4',
    tags: ['Leadership', 'Ministry', 'Growth'],
    category: { name: 'Ministry' },
    accessType: 'premium',
    chapterCount: 18,
    createdAt: '2025-08-05T11:15:00Z',
  },
  {
    id: '5',
    slug: 'psalms-commentary',
    title: 'Psalms: A Modern Commentary',
    author: { name: 'Dr. David Robinson' },
    description: 'Contemporary insights into the ancient wisdom of the Book of Psalms.',
    coverImageUrl: 'https://picsum.photos/300/400?random=5',
    tags: ['Psalms', 'Commentary', 'Worship'],
    category: { name: 'Biblical Studies' },
    accessType: 'free',
    chapterCount: 25,
    createdAt: '2025-11-28T09:45:00Z',
  },
  {
    id: '6',
    slug: 'gospel-truth',
    title: 'The Gospel Truth',
    author: { name: 'Rev. James Wilson' },
    description: 'Exploring the fundamental truths of the Christian gospel message.',
    coverImageUrl: 'https://picsum.photos/300/400?random=6',
    tags: ['Gospel', 'Truth', 'Salvation'],
    category: { name: 'Theology' },
    accessType: 'free',
    chapterCount: 10,
    createdAt: '2025-07-15T16:30:00Z',
  },
  {
    id: '7',
    slug: 'ancient-wisdom',
    title: 'Ancient Wisdom for Today',
    author: { name: 'Dr. Rachel Green' },
    description: 'Biblical wisdom literature applied to contemporary life challenges.',
    coverImageUrl: 'https://picsum.photos/300/400?random=7',
    tags: ['Wisdom', 'Proverbs', 'Practical'],
    category: { name: 'Devotional' },
    accessType: 'premium',
    chapterCount: 20,
    createdAt: '2025-06-22T13:10:00Z',
  },
  {
    id: '8',
    slug: 'acts-apostles-study',
    title: 'Acts of the Apostles: A Study Guide',
    author: { name: 'Pastor Thomas Brown' },
    description: 'Comprehensive study of the early church through the Book of Acts.',
    coverImageUrl: 'https://picsum.photos/300/400?random=8',
    tags: ['Acts', 'Church', 'Study Guide'],
    category: { name: 'Biblical Studies' },
    accessType: 'premium',
    chapterCount: 28,
    createdAt: '2025-05-18T10:25:00Z',
  },
  {
    id: '9',
    slug: 'christian-ethics',
    title: 'Christian Ethics in Practice',
    author: { name: 'Dr. Margaret White' },
    description: 'Navigating moral and ethical decisions through a biblical framework.',
    coverImageUrl: 'https://picsum.photos/300/400?random=9',
    tags: ['Ethics', 'Morality', 'Christian Living'],
    category: { name: 'Theology' },
    accessType: 'free',
    chapterCount: 16,
    createdAt: '2025-04-30T15:40:00Z',
  },
  {
    id: '10',
    slug: 'prophets-today',
    title: 'The Prophets Speak Today',
    author: { name: 'Rev. Daniel Lee' },
    description: 'Relevant messages from Old Testament prophets for modern times.',
    coverImageUrl: 'https://picsum.photos/300/400?random=10',
    tags: ['Prophets', 'Old Testament', 'Contemporary'],
    category: { name: 'Biblical Studies' },
    accessType: 'premium',
    chapterCount: 22,
    createdAt: '2025-03-12T12:00:00Z',
  },
  {
    id: '11',
    slug: 'spiritual-disciplines',
    title: 'Spiritual Disciplines Guide',
    author: { name: 'Pastor Linda Martinez' },
    description: 'Practical guide to developing spiritual disciplines for growth.',
    coverImageUrl: 'https://picsum.photos/300/400?random=11',
    tags: ['Disciplines', 'Growth', 'Prayer'],
    category: { name: 'Devotional' },
    accessType: 'free',
    chapterCount: 14,
    createdAt: '2025-02-08T09:20:00Z',
  },
  {
    id: '12',
    slug: 'kingdom-parables',
    title: 'Kingdom Parables Explained',
    author: { name: 'Dr. Steven Clark' },
    description: 'Deep dive into Jesus parables and their kingdom implications.',
    coverImageUrl: 'https://picsum.photos/300/400?random=12',
    tags: ['Parables', 'Kingdom', 'Teaching'],
    category: { name: 'Biblical Studies' },
    accessType: 'premium',
    chapterCount: 17,
    createdAt: '2025-01-20T14:35:00Z',
  },
];

const MOCK_CATEGORIES = [
  'All Categories',
  'Biblical Studies',
  'Theology',
  'Devotional',
  'Ministry',
];

function BookCard({ book, viewMode }) {
  const isOutline = viewMode === 'outline';

  if (isOutline) {
    return (
      <div className="flex items-center justify-between p-4 bg-card rounded-lg border border-border hover-lift">
        <div className="flex items-center space-x-4 flex-1">
          <img
            src={book?.coverImageUrl || 'https://picsum.photos/300/400?random=1'}
            alt={`${book?.title} book cover`}
            className="w-12 h-16 object-cover rounded"
          />
          <div className="flex-1">
            <h3 className="font-crimson font-semibold text-card-foreground">
              {book?.title}
            </h3>
            <p className="text-sm text-muted-foreground">
              {book?.author?.name}
            </p>
            <div className="flex items-center space-x-2 mt-1">
              <span
                className={`px-2 py-1 text-xs rounded-full ${
                  book?.accessType === 'free' ?'bg-success/20 text-success' :'bg-primary/20 text-primary'
                }`}
              >
                {book?.accessType === 'free' ? 'FREE' : 'PREMIUM'}
              </span>
              <div className="flex space-x-1">
                {book?.tags?.slice(0, 2)?.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
        <Link
          href={`/advanced-e-book-reader?book=${book?.slug}`}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 contemplative-transition"
        >
          Read Now
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border hover-lift overflow-hidden">
      <div className="relative">
        <img
          src={book?.coverImageUrl || 'https://picsum.photos/300/400?random=1'}
          alt={`${book?.title} book cover`}
          className="w-full h-48 object-cover"
        />
        <span
          className={`absolute top-3 right-3 px-3 py-1 text-xs font-medium rounded-full ${
            book?.accessType === 'free' ?'bg-success text-success-foreground' :'bg-primary text-primary-foreground'
          }`}
        >
          {book?.accessType === 'free' ? 'FREE' : 'PREMIUM'}
        </span>
      </div>
      <div className="p-4">
        <h3 className="font-crimson font-semibold text-lg text-card-foreground mb-1">
          {book?.title}
        </h3>
        <p className="text-muted-foreground text-sm mb-2">
          {book?.author?.name}
        </p>
        <p className="text-card-foreground text-sm mb-3 line-clamp-2">
          {book?.description}
        </p>
        <div className="flex flex-wrap gap-1 mb-3">
          {book?.tags?.slice(0, 3)?.map((tag) => (
            <span
              key={tag}
              className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded"
            >
              {tag}
            </span>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 text-xs text-muted-foreground">
            <span className="flex items-center space-x-1">
              <Icon name="BookOpenIcon" size={14} />
              <span>{book?.chapterCount || 0} chapters</span>
            </span>
          </div>
          <Link
            href={`/advanced-e-book-reader?book=${book?.slug}`}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 contemplative-transition text-sm"
          >
            Read Now
          </Link>
        </div>
      </div>
    </div>
  );
}

BookCard.propTypes = {
  book: PropTypes?.shape({
    id: PropTypes?.string?.isRequired,
    slug: PropTypes?.string?.isRequired,
    title: PropTypes?.string?.isRequired,
    author: PropTypes?.shape({
      name: PropTypes?.string?.isRequired,
    }),
    description: PropTypes?.string,
    coverImageUrl: PropTypes?.string,
    tags: PropTypes?.arrayOf(PropTypes?.string),
    category: PropTypes?.shape({
      name: PropTypes?.string,
    }),
    accessType: PropTypes?.oneOf(['free', 'premium'])?.isRequired,
    chapterCount: PropTypes?.number,
  })?.isRequired,
  viewMode: PropTypes?.oneOf(['grid', 'outline'])?.isRequired,
};

function ContinueReading() {
  // TODO: Replace with real user progress data
  const continueBook = {
    title: 'Chronicles of Faith',
    author: 'Dr. Sarah Mitchell',
    progress: 67,
    lastChapter: 'Chapter 2: Walking in Truth',
    slug: 'chronicles-of-faith',
  };

  return (
    <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h3 className="font-crimson font-semibold text-accent mb-1">
            Continue Reading
          </h3>
          <p className="text-foreground font-medium">{continueBook?.title}</p>
          <p className="text-muted-foreground text-sm">
            {continueBook?.author} • {continueBook?.lastChapter}
          </p>
          <div className="mt-2">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span>Progress</span>
              <span>{continueBook?.progress}%</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-accent h-2 rounded-full"
                style={{ width: `${continueBook?.progress}%` }}
              />
            </div>
          </div>
        </div>
        <Link
          href={`/advanced-e-book-reader?book=${continueBook?.slug}&continue=true`}
          className="ml-4 px-4 py-2 bg-accent text-accent-foreground rounded-lg hover:bg-accent/90 contemplative-transition"
        >
          Continue
        </Link>
      </div>
    </div>
  );
}

function DigitalBooksLibraryClient() {
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLetter, setSelectedLetter] = useState('ALL');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [selectedAccess, setSelectedAccess] = useState('All Access');
  const [selectedTags, setSelectedTags] = useState([]);
  const [sortBy, setSortBy] = useState('Title A-Z');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [showTagDropdown, setShowTagDropdown] = useState(false);

  const booksPerPage = 12;
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'?.split('');

  // Load mock data instead of fetching from Supabase
  useEffect(() => {
    // Simulate loading delay for realistic UX
    const timer = setTimeout(() => {
      try {
        setBooks(MOCK_BOOKS);
        setCategories(MOCK_CATEGORIES);
        setLoading(false);
      } catch (err) {
        console.error('Error loading mock data:', err);
        setError('Failed to load books');
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // All tags (memoized)
  const allTags = useMemo(
    () => [...new Set(books.flatMap((book) => book?.tags || []))],
    [books]
  );

  // Filtered books (memoized)
  const filteredBooks = useMemo(
    () =>
      books?.filter((book) => {
        const matchesSearch =
          book?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          book?.author?.name
            ?.toLowerCase()?.includes(searchQuery?.toLowerCase());

        const matchesLetter =
          selectedLetter === 'ALL' ||
          book?.title?.[0]?.toUpperCase() === selectedLetter;

        const matchesCategory =
          selectedCategory === 'All Categories' ||
          book?.category?.name === selectedCategory;

        const matchesAccess =
          selectedAccess === 'All Access' ||
          (selectedAccess === 'Free' && book?.accessType === 'free') ||
          (selectedAccess === 'Premium' && book?.accessType === 'premium');

        const matchesTags =
          selectedTags?.length === 0 ||
          selectedTags?.every((tag) => book?.tags?.includes(tag));

        return (
          matchesSearch &&
          matchesLetter &&
          matchesCategory &&
          matchesAccess &&
          matchesTags
        );
      }),
    [books, searchQuery, selectedLetter, selectedCategory, selectedAccess, selectedTags]
  );

  // Sorted books (memoized)
  const sortedBooks = useMemo(() => {
    const list = [...filteredBooks];

    return list?.sort((a, b) => {
      switch (sortBy) {
        case 'Title A-Z':
          return a?.title?.localeCompare(b?.title);
        case 'Title Z-A':
          return b?.title?.localeCompare(a?.title);
        case 'Newest': {
          const aDate = a?.createdAt ? new Date(a.createdAt) : 0;
          const bDate = b?.createdAt ? new Date(b.createdAt) : 0;
          return bDate - aDate;
        }
        case 'Most Read':
          return (b?.chapterCount || 0) - (a?.chapterCount || 0);
        default:
          return 0;
      }
    });
  }, [filteredBooks, sortBy]);

  // Pagination (memoized)
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(sortedBooks?.length / booksPerPage)),
    [sortedBooks?.length, booksPerPage]
  );

  const paginatedBooks = useMemo(() => {
    const startIndex = (currentPage - 1) * booksPerPage;
    return sortedBooks?.slice(startIndex, startIndex + booksPerPage);
  }, [sortedBooks, currentPage, booksPerPage]);

  // Clamp currentPage if filters reduce the number of pages
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleTagToggle = (tag) => {
    setSelectedTags((prev) =>
      prev?.includes(tag) ? prev?.filter((t) => t !== tag) : [...prev, tag]
    );
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedLetter('ALL');
    setSelectedCategory('All Categories');
    setSelectedAccess('All Access');
    setSelectedTags([]);
    setSortBy('Title A-Z');
    setCurrentPage(1);
  };

  // Smooth scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Pagination handlers
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      scrollToTop();
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      scrollToTop();
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading books...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <Icon
            name="ExclamationTriangleIcon"
            size={48}
            className="mx-auto text-destructive mb-4"
          />
          <h3 className="text-lg font-crimson font-semibold text-foreground mb-2">
            Error Loading Books
          </h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <button
            onClick={() => window.location?.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 contemplative-transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Main UI
  return (
    <div className="min-h-screen bg-background">
      <div className="px-4 lg:px-6 max-w-7xl mx-auto py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-crimson font-semibold text-foreground mb-2">
            Digital Books Library
          </h1>
          <p className="text-muted-foreground">
            Discover and explore our comprehensive collection of biblical
            literature and resources
          </p>
        </div>

        {/* Continue Reading */}
        <ContinueReading />

        {/* Search Bar */}
        <div className="relative mb-6">
          <Icon
            name="MagnifyingGlassIcon"
            size={20}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            placeholder="Search by title or author..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e?.target?.value);
              setCurrentPage(1);
            }}
            className="w-full pl-10 pr-4 py-3 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap gap-4 items-center mb-6 p-4 bg-card rounded-lg border border-border">
          {/* Access Filter */}
          <select
            value={selectedAccess}
            onChange={(e) => {
              setSelectedAccess(e?.target?.value);
              setCurrentPage(1);
            }}
            className="px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option>All Access</option>
            <option>Free</option>
            <option>Premium</option>
          </select>

          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e?.target?.value);
              setCurrentPage(1);
            }}
            className="px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {categories?.map((category) => (
              <option key={category}>{category}</option>
            ))}
          </select>

          {/* Tags Filter */}
          <div className="relative">
            <button
              onClick={() => setShowTagDropdown((prev) => !prev)}
              className="flex items-center space-x-2 px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <span>
                Tags {selectedTags?.length > 0 && `(${selectedTags?.length})`}
              </span>
              <Icon name="ChevronDownIcon" size={16} />
            </button>

            {showTagDropdown && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-popover border border-border rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                {allTags?.length > 0 ? (
                  allTags?.map((tag) => (
                    <label
                      key={tag}
                      className="flex items-center space-x-2 p-2 hover:bg-muted cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedTags?.includes(tag)}
                        onChange={() => handleTagToggle(tag)}
                        className="text-primary focus:ring-ring"
                      />
                      <span className="text-sm">{tag}</span>
                    </label>
                  ))
                ) : (
                  <div className="p-4 text-center text-muted-foreground text-sm">
                    No tags available
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sort Filter */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option>Title A-Z</option>
            <option>Title Z-A</option>
            <option>Newest</option>
            <option>Most Read</option>
          </select>

          {/* Clear All */}
          <button
            onClick={clearAllFilters}
            className="px-3 py-2 text-muted-foreground hover:text-foreground contemplative-transition"
          >
            Clear All
          </button>
        </div>

        {/* A-Z Navigation */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => {
              setSelectedLetter('ALL');
              setCurrentPage(1);
            }}
            className={`px-3 py-2 rounded-full text-sm font-medium contemplative-transition ${
              selectedLetter === 'ALL' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            ALL
          </button>
          {alphabet?.map((letter) => (
            <button
              key={letter}
              onClick={() => {
                setSelectedLetter(letter);
                setCurrentPage(1);
              }}
              className={`px-3 py-2 rounded-full text-sm font-medium contemplative-transition ${
                selectedLetter === letter
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {letter}
            </button>
          ))}
        </div>

        {/* View Toggle & Count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-muted-foreground">
            Showing {paginatedBooks?.length} of {sortedBooks?.length} books
          </p>

          <div className="flex items-center space-x-2 bg-muted rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded contemplative-transition ${
                viewMode === 'grid' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="Squares2X2Icon" size={20} />
            </button>
            <button
              onClick={() => setViewMode('outline')}
              className={`p-2 rounded contemplative-transition ${
                viewMode === 'outline' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="ListBulletIcon" size={20} />
            </button>
          </div>
        </div>

        {/* Selected Tags Display */}
        {selectedTags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {selectedTags?.map((tag) => (
              <span
                key={tag}
                className="flex items-center space-x-1 px-3 py-1 bg-primary/20 text-primary rounded-full text-sm"
              >
                <span>{tag}</span>
                <button
                  onClick={() => handleTagToggle(tag)}
                  className="hover:text-primary/70"
                >
                  <Icon name="XMarkIcon" size={14} />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Books Grid/List */}
        {paginatedBooks?.length === 0 ? (
          <div className="text-center py-12">
            <Icon
              name="BookOpenIcon"
              size={48}
              className="mx-auto text-muted-foreground mb-4"
            />
            <h3 className="text-lg font-crimson font-semibold text-foreground mb-2">
              No books found
            </h3>
            <p className="text-muted-foreground">
              Try adjusting your filters or search terms
            </p>
          </div>
        ) : (
          <div
            className={
              viewMode === 'grid' ?'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8' :'space-y-4 mb-8'
            }
          >
            {paginatedBooks?.map((book) => (
              <BookCard key={book?.id} book={book} viewMode={viewMode} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 pb-12">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                currentPage === 1
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed' :'bg-white text-[#4b3381] hover:bg-[#4b3381] hover:text-white shadow-md hover:shadow-xl transform hover:-translate-x-1'
              }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Previous
            </button>

            <div className="flex items-center gap-2">
              <span className="px-4 py-2 bg-[#4b3381] text-white rounded-xl font-bold shadow-lg">
                {currentPage}
              </span>
              <span className="text-gray-500 font-medium">of</span>
              <span className="px-4 py-2 bg-white text-gray-700 rounded-xl font-bold shadow-md">
                {totalPages}
              </span>
            </div>

            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                currentPage === totalPages
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed' :'bg-white text-[#4b3381] hover:bg-[#4b3381] hover:text-white shadow-md hover:shadow-xl transform hover:translate-x-1'
              }`}
            >
              Next
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DigitalBooksLibrary() {
  // Simple wrapper so you can later add layout / providers if needed
  return <DigitalBooksLibraryClient />;
}